import { Link, useSearchParams } from 'react-router-dom';
import { XCircle, RefreshCw, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/atoms/button';

type ErrorType = 'invalid' | 'expired' | 'notfound' | 'server' | 'default';

export default function ActivationFailedPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const errorType = (searchParams.get('error') as ErrorType) || 'default';

  const getErrorMessage = () => {
    switch (errorType) {
      case 'invalid':
        return t('activation.errorInvalid');
      case 'expired':
        return t('activation.errorExpired');
      case 'notfound':
        return t('activation.errorNotFound');
      case 'server':
        return t('activation.errorServer');
      default:
        return t('activation.errorDefault');
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-8 bg-background">
      <div className="w-full max-w-md text-center">
        {/* Logo */}
        <Link to="/" className="flex items-end justify-center gap-2 mb-8">
          <img src="/volovolo.png" alt="Volovolo" className="h-10 w-auto" />
          <span className="font-bold text-xl">Pro</span>
        </Link>

        {/* Error Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-red-100 flex items-center justify-center">
            <XCircle className="w-12 h-12 text-red-600" />
          </div>
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold mb-2">
          {t('activation.failedTitle')}
        </h1>

        {/* Error Message */}
        <p className="text-muted-foreground mb-6">{getErrorMessage()}</p>

        {/* Actions */}
        <div className="space-y-3">
          {(errorType === 'expired' || errorType === 'invalid') && (
            <Button asChild variant="default" className="w-full">
              <Link to="/resend-activation">
                <RefreshCw className="w-4 h-4 mr-2" />
                {t('activation.requestNewLink')}
              </Link>
            </Button>
          )}

          <Button asChild variant="outline" className="w-full">
            <Link to="/login">{t('activation.backToLogin')}</Link>
          </Button>

          <Button asChild variant="ghost" className="w-full">
            <a href="mailto:support@volovolo.com">
              <Mail className="w-4 h-4 mr-2" />
              {t('activation.contactSupport')}
            </a>
          </Button>
        </div>
      </div>
    </div>
  );
}
